"use client";

import { createContext, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark";

const ThemeContext = createContext<{
  theme: Theme;
  toggleTheme: () => void;
}>({
  theme: "light",
  toggleTheme: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

function applyTheme(next: Theme) {
  document.documentElement.setAttribute("data-theme", next);
  try {
    window.localStorage?.setItem?.("theme", next);
  } catch {
    /* privater Modus */
  }
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `theme=${next}; path=/; max-age=31536000; SameSite=Lax${secure}`;
}

/**
 * `initialTheme` kommt vom Server (Cookie `theme`): Das HTML trägt
 * data-theme bereits beim ersten Paint – kein Umschalten nach der
 * Hydration, kein Flackern für Dark-Mode-Nutzer, kein Hydration-Mismatch.
 */
export function ThemeProvider({
  children,
  initialTheme = "light",
}: {
  children: React.ReactNode;
  initialTheme?: Theme;
}) {
  const [theme, setTheme] = useState<Theme>(initialTheme);

  // Migration: Wahl lag bisher nur in localStorage → einmalig ins Cookie.
  useEffect(() => {
    try {
      const stored = window.localStorage?.getItem?.("theme");
      const hasCookie = document.cookie.split("; ").some((c) => c.startsWith("theme="));
      if (!hasCookie && (stored === "dark" || stored === "light") && stored !== initialTheme) {
        setTheme(stored);
        applyTheme(stored);
      }
    } catch {
      /* ignore */
    }
  }, [initialTheme]);

  const toggleTheme = () => {
    const next: Theme = theme === "light" ? "dark" : "light";
    setTheme(next);
    applyTheme(next);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
